import queries from '@apis/queries';
import AuthGuard, { AuthFlow } from '@components/AuthGuard';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useAppCustom from '@hooks/useAppCustom';
import FavoriteFill from '@icons/favorite/FavoriteFill';
import { ShopInMapRow } from '@models/shop';
import { useActivity, useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import Badge from '../Badge';
import useShopFavorite from './hooks/useShopFavorite';

interface Props {
    isOrder?: boolean;
    shop: ShopInMapRow;
    originalItemId?: string;
}

function ShopItem({ shop, isOrder, originalItemId }:Props) {
    const { push } = useFlow();
    const { t } = useTranslation('shopList');
    const {
        name, badges, distanceFromUser, address, thumbnailImageUrl, orderStatus, isMyShop,
    } = shop;
    const { shopImageShape } = useAppCustom().shop;
    const { toggleMyShop } = useShopFavorite(shop);
    const { data: shopItem } = useQuery({
        ...queries.items.shopItemFromShop(shop.id, originalItemId!),
        enabled: !!originalItemId,
    });
    const handleOpen = () => {
        if (shopItem) {
            push('ItemActivity', { itemId: shopItem.id, shopId: shop.id });
            return;
        }
        if (orderStatus === 'BREAK_TIME' || orderStatus === 'CLOSED') {
            push('ShopPrepareModal', { shopId: shop.id });
        } else if (isOrder) {
            push('ShopLocationBottomSheet', {
                shopId: shop.id,
                lat: shop.lat,
                lng: shop.lon,
            });
        } else if (orderStatus === 'OPEN') {
            push('ShopDetailBottomSheet', { shopId: shop.id });
        }
    };
    const flows: AuthFlow[] = [
        { activityName: 'ShopListActivity', activityParams: { type: 'LIST' } },
    ];
    return (
        <Row spacing={14}
            className={styles.container}
            onClick={handleOpen}
            fullWidth
            align='flex-start'
            aria-label='shop-item'
            css={pressActionEffect} >
            <ThumbnailContainer shape={shopImageShape} >
                <Thumbnail src={thumbnailImageUrl}
                    shape={shopImageShape}
                    alt={name}
                    width={212}
                    height={212}
                    className={styles.thumbnail}/>
                {orderStatus !== 'OPEN' && (
                    <div className={styles.closed}>
                        <Text fontWeight='bold'
                            color={colors.white}>
                            {t('shopItem.preparing')}
                        </Text>
                    </div>
                )}
            </ThumbnailContainer>
            <Column className={styles.content}>
                <Row fullWidth
                    justify='space-between' >
                    <Text size='lg' fontWeight='bold' css={css`
                        flex: 1;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    `}>{name}</Text>
                    <AuthGuard className={styles.circleFavorite}
                        flows={flows} >
                        <div onClick={toggleMyShop}>
                            <FavoriteFill size={28}
                                color={isMyShop ? colors.red : colors.gray4}/>
                        </div>
                    </AuthGuard>
                </Row>
                <Text color={colors.gray8}>
                    {address}
                </Text>
                <Row className={styles.footer}
                    align='flex-start'
                    fullWidth
                    spacing={8} >
                    {distanceFromUser && (
                        <Text color={colors.gray6}
                            fontWeight='bold'
                            className={styles.distance} >
                            {distanceFromUser}
                        </Text>
                    )}
                    <BadgeContainer>
                        {badges.map((badge, index) => (
                            <Badge key={`${badge} ${index}`} >
                                {badge}
                            </Badge>
                        ))}
                    </BadgeContainer>
                </Row>
            </Column>
        </Row>
    );
}

const ThumbnailContainer = styled.div<{
    shape: 'CIRCLE' | 'RECTANGLE';
}>`
    border: 1px solid var(--color-gray4);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${(props) => (props.shape === 'RECTANGLE' ? '12px' : '50%')};
    position: relative;
    max-width: 106px;
    min-width: 106px;
    overflow: hidden;
`;

const Thumbnail = styled(Image)<{
    shape: 'CIRCLE' | 'RECTANGLE';
}>`
    border: 1px solid var(--color-gray4);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${(props) => (props.shape === 'RECTANGLE' ? '12px' : '50%')};
    overflow: hidden;
    position: relative;
    max-width: 106px;
    min-width: 106px;
`;

const BadgeContainer = styled.div`
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

export default ShopItem;
